<template>
  <v-container fluid>
    <PageHeader title="Connection" icon="mdi-domain">
      <template #actions>
        <PartyStatus v-if="party" :party="party" />
        <ActionDropdown :actions="actions" :performAction="performAction" class="ml-2" />
      </template>
    </PageHeader>
    <v-card class="elevation-1 card pa-4 card-form" v-if="!loading">
      <v-form ref="form">
        <v-card-text>
          <EntityForm v-model:entity="party" :inputFields="PARTY_FIELDS" />
        </v-card-text>
      </v-form>
      <v-card-actions>
        <v-spacer />
        <v-btn @click="save()" :disabled="loading" size="large" color="primary" variant="flat">Save</v-btn>
        <v-btn @click="cancel()" :disabled="loading" size="large" variant="flat">Cancel</v-btn>
      </v-card-actions>
    </v-card>
    <LoadingProgress :loading="loading" />
  </v-container>
</template>

<script>
import partyService from "@/services/partyService";
import PageHeader from "@/components/PageHeader.vue";
import partyStatusService from "@/services/partyStatusService";
import partyTypeList from "@/constants/partyTypeList";
import validationService from "@/services/validationService";
import Auth0Mixin from "../Auth0Mixin";
import EntityForm from "@/components/EntityForm.vue";
import ActionDropdown from "@/components/ActionDropdown.vue";
import orgUnitService from "@/services/admin/orgUnitService";
import PartyStatus from "@/components/PartyStatus.vue";
import terminologyService from "@/services/terminologyService.js";
import terminology from "@/constants/terminology";
import LoadingProgress from "@/components/LoadingProgress.vue";

const partyTypeListWithTerminology = JSON.parse(JSON.stringify(partyTypeList));

const ACTION_DELETE = "Delete";
const ACTION_SAVE = "Save";

const PARTIES_URL = "/admin/connections";

const requiredRule = [v => !!v || "Required"];

const orgUnitField = { label: "", attribute: "companyOrgUnitIds", type: "multi-select", items: [] };
const PARTY_FIELDS = [
  { label: "Type", attribute: "partyTypeId", rules: requiredRule, type: "select", items: partyTypeListWithTerminology },
  { label: "Name", attribute: "name", rules: requiredRule, type: "text" },
  { label: "Known As", attribute: "nameTwo", type: "text", maxlength: 250 },
  { label: "Connection Number", attribute: "number", rules: requiredRule, type: "text" },
  orgUnitField,
  { label: "Effective From Date", attribute: "effectiveFromDate", type: "date", showDatePicker: false },
  { label: "Effective Thru Date", attribute: "effectiveThruDate", type: "date", showDatePicker: false },

  // Contact
  { label: "Contact", attribute: "contact", type: "header" },
  {
    label: "Emails",
    attribute: "emailsAsString",
    rules: requiredRule,
    type: "text",
    maxlength: 500,
    hint: "Specify multiple emails with spaces. For example: john@mycompany.com sue@mycompany.com",
  },
  { label: "Phone number", attribute: "phone", type: "text", maxlength: 16 },
  { label: "Extension", attribute: "ext", type: "text", maxlength: 8 },
  { label: "Cell Phone Number", attribute: "cell", type: "text", maxlength: 16 },

  { label: "Active", attribute: "isActive", type: "checkbox" },
];

export default {
  name: "Connection",
  mixins: [Auth0Mixin],
  components: { EntityForm, ActionDropdown, PartyStatus, LoadingProgress, PageHeader },
  data() {
    return {
      party: null,
      loading: false,
      actions: [ACTION_SAVE, ACTION_DELETE],
      PARTY_FIELDS,
      partyTypeListWithTerminology,
    };
  },

  methods: {
    async init() {
      this.loading = true;
      partyTypeListWithTerminology.forEach(pt => {
        if (pt.terminologyId) pt.name = terminologyService.lookup(pt.terminologyId);
      });

      orgUnitField.label = terminologyService.lookup(terminology.ORG_UNIT);

      this.PARTY_FIELDS[0].readonly = true;

      this.party = await partyService.get(this.$route.params.id);
      this.party.emailsAsString = this.party.emails.join(" ");
      this.party.effectiveFromDate = this.party.effectiveFromDate ? this.party.effectiveFromDate.substr(0, 10) : null;
      this.party.effectiveThruDate = this.party.effectiveThruDate ? this.party.effectiveThruDate.substr(0, 10) : null;

      const result = await orgUnitService.getList(true);
      orgUnitField.items = result.items
        .filter(orgUnit => orgUnit.isActive === true)
        .map(o => ({ id: o.id, name: o.name }));

      this.loading = false;
    },
    async performAction(action) {
      if (action === ACTION_DELETE) {
        if (
          partyStatusService.isConnected(this.party) ||
          partyStatusService.IsInviteSent(this.party) ||
          partyStatusService.IsConnecting(this.party)
        ) {
          this.$root.$snackbar.error(`Connected connection or connections with pending invites cannot be deleted`);
        } else {
          await partyService.delete(this.party.id);
          this.$root.$snackbar.message(`Connection Deleted`);
          this.$router.push(PARTIES_URL);
        }
      }
      if (action === ACTION_SAVE) {
        this.save();
      }
    },

    convertEmailsToList() {
      let es = this.party.emailsAsString.trim();
      if (es.length === 0) {
        this.party.emails = [];
        return true;
      }

      var emails = es.split(/\s+/);

      let invalidEmail = emails.find(email => !validationService.isvalidEmail(email));

      if (invalidEmail != undefined) {
        this.$root.$snackbar.error(`${invalidEmail} is not a valid email`);
        return false;
      }

      this.party.emails = emails;
      return true;
    },

    async save() {
      const { valid, errors } = await this.$refs.form.validate();
      if (!valid) {
        const errorFields = errors
          .map(e => {
            const el = document.getElementById(e.id);
            const label = el
              ?.closest(".v-input")
              ?.querySelector(".v-label")
              ?.textContent?.trim();
            return label || e.id;
          })
          .join(", ");
        this.$root.$snackbar.error("Please fix validation errors: " + errorFields);
        return;
      }

      if (this.party.effectiveFromDate && this.party.effectiveThruDate) {
        if (!validationService.isValidDateRange(this.party.effectiveFromDate, this.party.effectiveThruDate)) {
          window.scrollTo(0, 0);
          this.$root.$snackbar.error("Effective From Date must be before Effective Thru Date");
        }
      }

      if (!this.party.companyOrgUnitIds || this.party.companyOrgUnitIds.length === 0) {
        window.scrollTo(0, 0);
        this.$root.$snackbar.error("You must select at least 1 " + terminologyService.lookup(terminology.ORG_UNIT));
        return;
      }

      if (!this.convertEmailsToList()) {
        return;
      }

      this.loading = true;

      try {
        await partyService.update(this.party);
      } catch (err) {
        this.loading = false;
        this.$root.$snackbar.exception(err);
        return;
      }

      this.$root.$snackbar.message(`Connection Saved`);
      this.$router.push(PARTIES_URL);
    },
    cancel() {
      this.$router.push(PARTIES_URL);
    },
  },
};
</script>
<style scoped>
.invite {
  border: 2px solid rgba(var(--v-border-color), var(--v-border-opacity));
}
</style>
